import { useMemo } from 'react'
import styles from './SquareTemplate.module.scss'
import commonStyles from '../../TableCardsManagement.module.scss'
import TableCardQrCode from '../../components/TableCardQrCode/TableCardQrCode'
import Przelewy24Icon from '../assets/icons/przelewy24.svg?react'
import VisaIcon from '../assets/icons/visa.svg?react'
import MastercardIcon from '../assets/icons/masterCard.svg?react'
import ApplePayIcon from '../assets/icons/applePay.svg?react'
import GooglePayIcon from '../assets/icons/googlePay.svg?react'
import PaypalIcon from '../assets/icons/payPal.svg?react'
import BlikIcon from '../assets/icons/blik.svg?react'
import NfcIcon from '../assets/icons/nfc.svg?react'

interface SquareTemplateProps {
  withNfc?: boolean
  tableNumber: number
  qrCodeValue?: string
  languageCode?: 'en' | 'pl'
  logoImageUrl: string
  backgroundColorValue?: string
  contentColor?: string
  contrastColor?: string
  backgroundImage?: string | null
  width: number
  height: number
}

const BASE_TEMPLATE_WIDTH = 508
const BASE_TEMPLATE_HEIGHT = 508
const BASE_ASPECT_RATIO = BASE_TEMPLATE_WIDTH / BASE_TEMPLATE_HEIGHT // 1:1
const ASPECT_RATIO_TOLERANCE = 0.1 // allow up to ~10% deviation

function SquareTemplate(props: SquareTemplateProps) {
  const {
    withNfc,
    tableNumber,
    qrCodeValue,
    languageCode = 'en',
    logoImageUrl,
    backgroundColorValue,
    contentColor,
    contrastColor,
    backgroundImage,
    width,
    height
  } = props

  const {
    templateScale,
    offsetLeft,
    offsetTop,
    isAspectRatioValid,
    aspectRatioDeviationPercent
  } = useMemo(() => {
    const fitScale =
      Math.min(width / BASE_TEMPLATE_WIDTH, height / BASE_TEMPLATE_HEIGHT) || 0

    const computedOffsetLeft =
      (width - BASE_TEMPLATE_WIDTH * fitScale) / 2 / fitScale
    const computedOffsetTop =
      (height - BASE_TEMPLATE_HEIGHT * fitScale) / 2 / fitScale

    const userAspectRatio =
      width > 0 && height > 0 ? width / height : BASE_ASPECT_RATIO
    const relativeDeviation =
      Math.abs(userAspectRatio - BASE_ASPECT_RATIO) / BASE_ASPECT_RATIO

    return {
      templateScale: fitScale,
      offsetLeft: computedOffsetLeft,
      offsetTop: computedOffsetTop,
      isAspectRatioValid: relativeDeviation <= ASPECT_RATIO_TOLERANCE,
      aspectRatioDeviationPercent: relativeDeviation * 100
    }
  }, [width, height])

  return (
    <div
      className={styles.squareTemplateWrapper}
      style={{
        position: 'relative',
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: backgroundColorValue,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-has-aspect-ratio-warning={isAspectRatioValid ? undefined : 'true'}
      title={
        isAspectRatioValid
          ? undefined
          : `Size deviates ${aspectRatioDeviationPercent.toFixed(1)}% from 1:1`
      }
      aria-invalid={isAspectRatioValid ? undefined : true}
    >
      <div
        className={commonStyles.contrastOverlay}
        style={{ backgroundColor: contrastColor }}
      />
      <div
        className={styles.squareContentWrapper}
        style={{
          position: 'absolute',
          left: `${offsetLeft}px`,
          top: `${offsetTop}px`,
          width: `${BASE_TEMPLATE_WIDTH}px`,
          height: `${BASE_TEMPLATE_HEIGHT}px`,
          zoom: templateScale
        }}
      >
        <div className={styles.templateContent}>
          <div
            className={styles.templateHeader}
            style={{ justifyContent: withNfc ? 'space-between' : 'center' }}
          >
            <div className={styles.templateLogoAndTabeNumberWrapper}>
              <img className={styles.logoImage} src={logoImageUrl} alt="Logo" />
              <p
                className={`${styles.tableNumber} ${
                  withNfc ? styles.tableNumberWithNFC : ''
                } display-medium`}
                style={{ color: contentColor }}
              >
                {tableNumber}
              </p>
            </div>
            {withNfc && (
              <div className={styles.nfcIconWrapper}>
                <NfcIcon
                  className={styles.nfcIcon}
                  style={{ color: contentColor }}
                />
              </div>
            )}
          </div>
          <div className={styles.templateQrCodeAndDescriptionWrapper}>
            <p
              className={`${styles.templateDescription} display-medium`}
              style={{ color: contentColor }}
            >
              {languageCode === 'pl' ? 'Skanuj kod' : 'Scan & Pay'}
            </p>
            <div
              className={styles.qrCodeImageWrapper}
              style={{
                backgroundColor: contentColor,
                marginTop: '3.25rem'
              }}
            >
              <TableCardQrCode
                className={styles.qrCodeImage}
                value={qrCodeValue}
              />
            </div>
            <div className={styles.templateDescriptionWithMenu}>
              <p
                className={`${styles.templateDescriptionText} title-large`}
                style={{ color: contentColor }}
              >
                {languageCode === 'pl'
                  ? 'Zapłać bezpiecznie z'
                  : 'Pay securely with'}
              </p>
              <p
                className={`${styles.templateMenuText} logo-font-template-small`}
                style={{ color: contentColor }}
              >
                MENU!
              </p>
            </div>
          </div>
          <div className={styles.paymentMethodsIcons}>
            <Przelewy24Icon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <BlikIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <VisaIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <MastercardIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <ApplePayIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <GooglePayIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <PaypalIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default SquareTemplate
